import { types } from 'mobx-state-tree';
import type { Instance } from 'mobx-state-tree/dist/core/type/type';

import { AcademicYear, StudentProfileStatus, UserRole } from '@/enums/users';
import { ApplicationModel } from '@/models/applications';
import { UniversityModel } from '@/models/common';
import { FeedbackModel } from '@/models/feedback';
import { edges, nullOrUndefined } from '@/models/utils';
import { BaseModel } from '@/stores/shared';

export const StudentUniversityModel = types.compose(
  BaseModel,
  types
    .model({
      courseOfStudy: types.maybe(types.string),
      academicYear: types.maybe(
        types.enumeration<string>(Object.keys(AcademicYear)),
      ),
      programme: types.maybe(types.string),
      graduationYear: types.maybe(types.number),
      university: types.maybeNull(UniversityModel),
    })
    .views((self) => ({
      get academicYearEnum(): AcademicYear | undefined {
        return self.academicYear ? AcademicYear[self.academicYear] : undefined;
      },
    })),
);

export type StudentUniversityModelType = Instance<
  typeof StudentUniversityModel
> & {};

export const PortfolioRecordModel = types.compose(
  BaseModel,
  types.model({
    year: types.maybe(types.number),
    projectTitle: types.maybe(types.string),
    description: types.maybe(types.frozen()),
    company: types.maybe(types.string),
    link: types.maybe(types.string),
  }),
);

export type PortfolioRecordModelType = Instance<
  typeof PortfolioRecordModel
> & {};

export const StudentProfileModel = types.compose(
  BaseModel,
  types.model({
    tz: types.maybe(types.string),
    ICPCParticipant: types.maybe(types.boolean),
    ICPCContest: types.maybe(types.string),
    about: types.maybe(types.frozen()),
    portfolioLink: types.maybe(types.string),
    status: types.maybe(
      types.enumeration<StudentProfileStatus>(
        Object.values(StudentProfileStatus),
      ),
    ),
    updateTime: types.maybe(types.string),
    universities: types.maybe(edges(StudentUniversityModel)),
    portfolioRecords: types.maybe(edges(PortfolioRecordModel)),
    user: types.late(() => types.maybe(UserModel)),
  }),
);

export type StudentProfileModelType = Instance<typeof StudentProfileModel> & {};

export const MentorProfileModel = types.compose(
  BaseModel,
  types.model({
    tz: types.maybe(types.string),
    extendedActionsDeadline: nullOrUndefined(types.string),
  }),
);

export type MentorProfileModelType = Instance<typeof MentorProfileModel> & {};

export const UserModel = types.compose(
  BaseModel,
  types.model({
    firstName: types.maybe(types.string),
    lastName: types.maybe(types.string),
    email: types.maybe(types.string),
    photo: nullOrUndefined(types.string),
    role: types.maybe(types.enumeration<UserRole>(Object.values(UserRole))),
    hasPendingInterviews: types.maybe(types.boolean),
    isJbEmployee: types.maybe(types.boolean),
    studentprofile: nullOrUndefined(StudentProfileModel),
    mentorprofile: nullOrUndefined(MentorProfileModel),
    applications: types.late(() => types.maybe(edges(ApplicationModel))),
    feedback: types.maybe(edges(FeedbackModel)),
  }),
);

export type UserModelType = Instance<typeof UserModel> & {};
